import { useEffect, useState } from "react";
import { Users as UsersIcon, Shield, Ban, Trash2, Crown, Eye, Wrench } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const ROLE_META = {
  owner: { icon: Crown, color: "text-amber-400", label: "Owner" },
  admin: { icon: Wrench, color: "text-sky-400", label: "Admin" },
  readonly: { icon: Eye, color: "text-zinc-400", label: "Read-only" },
};

export default function Users() {
  const [users, setUsers] = useState(null);
  const { user: me } = useAuth();
  const isOwner = me?.role === "owner";

  const load = () => api.get("/users").then(setUsers).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const setRole = async (u, role) => {
    try { await api.patch(`/users/${u.user_id}`, { role }); toast.success(`${u.email} → ${role}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleDisabled = async (u) => {
    try { await api.patch(`/users/${u.user_id}`, { disabled: !u.disabled }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (u) => {
    try { await api.del(`/users/${u.user_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!users) return <Loading label="Loading users" />;

  return (
    <div data-testid="users-page">
      <PageHeader title="Users & Auth" breadcrumb="cannon / users" subtitle="Owner · Admin · Read-only · Google SSO · first sign-in provisions Owner" />

      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800 bg-zinc-900/50 text-left text-xs font-semibold uppercase tracking-widest text-zinc-500">
            <th className="px-4 py-2.5">User</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5 text-right">Actions</th></tr></thead>
          <tbody>
            {users.map((u) => {
              const rm = ROLE_META[u.role] || ROLE_META.readonly;
              return (
                <tr key={u.user_id} className="border-b border-zinc-800/70 hover:bg-zinc-900/40" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {u.picture ? <img src={u.picture} className="h-7 w-7 rounded-full" alt="" /> : <div className="h-7 w-7 rounded-full bg-zinc-800" />}
                      <div><div className="text-zinc-200">{u.name || "—"}</div><div className="font-mono text-xs text-zinc-500">{u.email}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {isOwner && u.user_id !== me.user_id ? (
                      <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                        <SelectTrigger className="w-32 h-8 bg-zinc-900 border-zinc-800" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0c0c0e] border-zinc-800">
                          <SelectItem value="owner">Owner</SelectItem><SelectItem value="admin">Admin</SelectItem><SelectItem value="readonly">Read-only</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={cn("inline-flex items-center gap-1.5 font-mono text-xs", rm.color)}><rm.icon className="h-3.5 w-3.5" /> {rm.label}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{u.disabled ? <span className="text-rose-400">disabled</span> : <span className="text-emerald-400">active</span>}</td>
                  <td className="px-4 py-3 text-right">
                    {isOwner && u.role !== "owner" && (
                      <>
                        <button onClick={() => toggleDisabled(u)} title={u.disabled ? "Enable" : "Disable"} className="p-1 text-zinc-500 hover:text-amber-400"><Ban className="h-4 w-4" /></button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><button data-testid={`delete-user-${u.email}`} className="p-1 text-zinc-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#0c0c0e] border-zinc-800">
                            <AlertDialogHeader><AlertDialogTitle>Delete {u.email}?</AlertDialogTitle><AlertDialogDescription>Revokes all sessions and removes the account.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del(u)} className="bg-rose-600 hover:bg-rose-500">Delete</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!isOwner && <p className="text-xs text-zinc-600 mt-3 font-mono">Only the Owner can modify roles and accounts.</p>}
    </div>
  );
}
